// Push Notification Service for Coach Coop SHRED
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

class NotificationService {
  constructor() {
    this.swRegistration = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Initialize service worker
  async init() {
    if (!this.isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.swRegistration);
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) return 'denied';

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }

  // Check if notifications are enabled
  isEnabled() {
    return Notification.permission === 'granted';
  }

  // Subscribe to push notifications
  async subscribe() {
    if (!this.swRegistration) {
      await this.init();
    }

    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    try {
      // For demo purposes, we'll use a simple subscription
      // In production, you'd use VAPID keys for web push
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        // In production, replace with your VAPID public key
        applicationServerKey: this.urlBase64ToUint8Array(
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
        )
      });

      // Save subscription to backend
      await axios.post(`${API}/notifications/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        }
      });

      console.log('Push subscription successful');
      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    if (!this.swRegistration) return;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/notifications/unsubscribe`);
        console.log('Unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Unsubscribe failed:', error);
    }
  }

  // Show local notification (doesn't require push server)
  async showLocalNotification(title, options = {}) {
    if (!this.isEnabled()) {
      console.log('Notifications not enabled');
      return;
    }

    if (!this.swRegistration) {
      await this.init();
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      tag: 'coach-coop-local',
      requireInteraction: false,
      ...options
    };

    return this.swRegistration.showNotification(title, defaultOptions);
  }

  // Schedule a notification for later
  scheduleNotification(title, body, delayMs) {
    if (!this.swRegistration) return;

    this.swRegistration.active.postMessage({
      type: 'SCHEDULE_NOTIFICATION',
      title,
      body,
      delay: delayMs
    });
  }

  // Send workout reminder
  async sendWorkoutReminder(dayNumber) {
    await this.showLocalNotification('Time to Train! 💪', {
      body: `Day ${dayNumber} is waiting. Don't break your streak!`,
      tag: 'workout-reminder',
      requireInteraction: true,
      actions: [
        { action: 'start', title: 'Start Workout' },
        { action: 'later', title: 'Remind Later' }
      ],
      data: { url: `/workout/${dayNumber}` }
    });
  }

  // Send streak alert
  async sendStreakAlert(streakCount) {
    await this.showLocalNotification('Streak Alert! 🔥', {
      body: `Your ${streakCount}-day streak is at risk! Complete today's workout to keep it going.`,
      tag: 'streak-alert',
      requireInteraction: true,
      actions: [
        { action: 'start', title: 'Start Workout' }
      ]
    });
  }

  // Send motivation message
  async sendMotivationMessage(message) {
    await this.showLocalNotification('Coach Coop Says:', {
      body: message,
      tag: 'motivation',
      requireInteraction: false
    });
  }

  // Send workout complete notification
  async sendWorkoutComplete(dayNumber, streak) {
    await this.showLocalNotification('Workout Complete! 🎉', {
      body: `Day ${dayNumber} done! ${streak > 1 ? `You're on a ${streak}-day streak!` : 'Keep showing up!'}`,
      tag: 'workout-complete',
      requireInteraction: false
    });
  }

  // Utility: Convert VAPID key to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Utility: Convert ArrayBuffer to Base64
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
